import { Button, SlideoutMenu, Tabs } from "@light-technology/components";
import { type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "../../state/api";
import {
  type AppResponse,
  type PlanDetailResponse,
} from "../../state/api.types";
import { planEditLink, plansLink } from "../../utilities/links";
import { usePlanComparison } from "../PlanComparisonTable/common";
import { toServiceTerritory, toTermMonths } from "../PlanEditPage/common";
import { DEFAULT_SERVICE_TERRITORY, TABS } from "./common";
import { Header } from "./Header";
import { LinkedPrograms } from "./LinkedPrograms";
import { PlanCompare } from "./PlanCompare";
import { DownloadEFLs, PlanDesign } from "./PlanDesign";

type PlanSlideoutProps = {
  app: AppResponse;
  children: ReactNode | ReactNode[];
};

type PlanContentProps = {
  app: AppResponse;
  plan: PlanDetailResponse;
};

const PlanContent = ({ app, plan }: PlanContentProps) => {
  const [searchParams] = useSearchParams();
  const serviceTerritory = toServiceTerritory(
    searchParams.get("service_territory") ?? DEFAULT_SERVICE_TERRITORY,
  );
  const termMonths = toTermMonths(
    searchParams.get("term_months") ?? plan.term_months.toString(),
  );
  const planComparison = usePlanComparison(plan, serviceTerritory, termMonths);

  return (
    <>
      <Tabs.Panel id={TABS[0].id} className="h-full">
        <div className="flex flex-col gap-6xl">
          <PlanDesign app={app} plan={plan} />
          <LinkedPrograms app={app} programs={plan.linked_programs} />
        </div>
      </Tabs.Panel>
      <Tabs.Panel id={TABS[1].id}>
        <PlanCompare plan={plan} planComparison={planComparison} />
      </Tabs.Panel>
    </>
  );
};

type PlanOverviewProps = {
  app: AppResponse;
  planId: string;
  close: () => void;
};

const PlanOverview = ({ app, planId, close }: PlanOverviewProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") ?? TABS[0].id;

  const getPlan = api.useGetPlanQuery(planId);
  const getConfiguration = api.useGetConfigurationQuery(app.uuid);

  const plan = getPlan.data;
  const configuration = getConfiguration.data;

  return (
    <>
      <Tabs
        selectedKey={tab}
        onSelectionChange={(newTab) => {
          setSearchParams({ tab: newTab.toString() });
        }}
        className="h-full"
      >
        <div className="flex h-full flex-col">
          <Header close={close} plan={plan} configuration={configuration} />
          {plan && configuration && (
            <SlideoutMenu.Content className="h-full pt-3xl">
              <PlanContent app={app} plan={plan} />
            </SlideoutMenu.Content>
          )}
        </div>
      </Tabs>
      <SlideoutMenu.Footer className="flex items-center justify-end gap-lg">
        {plan && <DownloadEFLs plan={plan} />}
        <Button href={planEditLink(app.slug, planId)} size="sm">
          Edit plan
        </Button>
      </SlideoutMenu.Footer>
    </>
  );
};

export const PlanSlideout = (props: PlanSlideoutProps) => {
  const { app, children } = props;
  const navigate = useNavigate();
  const { planId } = useParams();

  const close = () => {
    navigate(plansLink(app.slug));
  };

  return (
    <SlideoutMenu.Trigger
      isOpen={!!planId}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          close();
        }
      }}
    >
      {children}
      <SlideoutMenu isDismissable className="max-w-xl">
        {planId && <PlanOverview app={app} planId={planId} close={close} />}
      </SlideoutMenu>
    </SlideoutMenu.Trigger>
  );
};
